import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { buildReferences, getReferences } from "../api/references";
import { getProject } from "../api/projects";
import { getSources } from "../api/sources";
import type { Reference } from "../types";

export function useReferences(projectId: string | undefined) {
  return useQuery<Reference[]>({
    queryKey: ["references", projectId],
    queryFn: () => getReferences(projectId!),
    enabled: !!projectId,
    staleTime: 10_000,
  });
}

export function useBuildReferences(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => buildReferences(projectId),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: ["references", projectId] });
    },
    onSuccess: async () => {
      const [references, project, selectedSources] = await Promise.allSettled([
        getReferences(projectId),
        getProject(projectId),
        getSources(projectId, { selected: true }),
      ]);

      if (references.status === "fulfilled") {
        qc.setQueryData(["references", projectId], references.value);
      }
      if (project.status === "fulfilled") {
        qc.setQueryData(["project", projectId], project.value);
      }
      if (selectedSources.status === "fulfilled") {
        qc.setQueryData(["sources", projectId, true], selectedSources.value);
      }

      await Promise.all([
        qc.invalidateQueries({ queryKey: ["references", projectId] }),
        qc.invalidateQueries({ queryKey: ["sources", projectId] }),
        qc.invalidateQueries({ queryKey: ["project", projectId] }),
      ]);
    },
    onError: () => {
      qc.invalidateQueries({ queryKey: ["references", projectId] });
    },
  });
}
